// hooks/useDataEntryRealtime.ts - Fixed self-inclusion in presence
import { useEffect, useCallback, useRef, useState } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useUserProfile } from '../../../shared/hooks/useUserProfile';
import type { TableContent, EditingState } from '../../../shared/lib/supabase';

interface PresenceUser {
  userId: string;
  userName: string;
  userColor: string;
  online_at: string;
}

export interface LocalContentChange {
  userId: string;
  userName: string;
  userColor: string;
  tableId: string;
  cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes';
  rowIndex?: number;
  colIndex?: number;
  value: string;
  timestamp: string;
  isTemporary: boolean;
}

interface UseDataEntryRealtimeProps {
  entryId: string;
  projectId?: string;
  onContentUpdate?: (content: TableContent, userId: string) => void;
  onConnectionChange?: (status: string) => void;
  onLiveContentChange?: (change: LocalContentChange) => void;
}

export function useDataEntryRealtime({
  entryId,
  projectId,
  onContentUpdate,
  onConnectionChange,
  onLiveContentChange
}: UseDataEntryRealtimeProps) {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'reconnecting' | 'disconnected'>('disconnected');
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [editingStates, setEditingStates] = useState<EditingState[]>([]);
  const [liveContentChanges, setLiveContentChanges] = useState<LocalContentChange[]>([]);
  const [userColor, setUserColor] = useState<string>('#3B82F6');
  
  const channelRef = useRef<any>(null);
  const presenceChannelRef = useRef<any>(null);
  const currentContentRef = useRef<TableContent | null>(null);
  const isConnectedRef = useRef(false);
  const userIdRef = useRef<string | null>(null); // Add stable user ID ref

  // Update user ID ref when user changes
  useEffect(() => {
    userIdRef.current = user?.id || null;
  }, [user?.id]);

  // Stable callbacks that won't change on every render
  const onContentUpdateRef = useRef(onContentUpdate);
  const onConnectionChangeRef = useRef(onConnectionChange);
  const onLiveContentChangeRef = useRef(onLiveContentChange);
  
  useEffect(() => {
    onContentUpdateRef.current = onContentUpdate;
    onConnectionChangeRef.current = onConnectionChange;
    onLiveContentChangeRef.current = onLiveContentChange;
  }, [onContentUpdate, onConnectionChange, onLiveContentChange]);

  // Generate user color only once
  useEffect(() => {
    if (user?.id && userColor === '#3B82F6') {
      const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#F97316', '#06B6D4', '#84CC16'];
      const colorIndex = user.id.charCodeAt(0) % colors.length;
      setUserColor(colors[colorIndex]);
    }
  }, [user?.id, userColor]);

  // Stable display name function
  const getDisplayName = useCallback(() => {
    const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
    return fullName || user?.email?.split('@')[0] || 'Anonymous';
  }, [profile?.first_name, profile?.last_name, user?.email]);

  // Stable connection status update
  const updateConnectionStatus = useCallback((status: 'connecting' | 'connected' | 'reconnecting' | 'disconnected') => {
    setConnectionStatus(status);
    onConnectionChangeRef.current?.(status);
  }, []);

  // Set up real-time subscription for data changes
  useEffect(() => {
    if (!entryId || !user?.id || isConnectedRef.current) {
      return;
    }

    console.log(`🔄 Setting up data entry real-time for entry: ${entryId}`);
    updateConnectionStatus('connecting');
    isConnectedRef.current = true;

    const channel = supabase
      .channel(`data_entry_changes_${entryId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'data_entries',
        filter: `id=eq.${entryId}`
      }, (payload) => {
        console.log('📊 Data entry updated by another user:', payload);
        if (payload.new.updated_by !== user.id && onContentUpdateRef.current) {
          onContentUpdateRef.current(payload.new.content, payload.new.updated_by);
        }
      })
      .on('broadcast', { event: 'content_change' }, (payload) => {
        const data = payload.payload;
        if (data.userId !== user.id && onContentUpdateRef.current) {
          onContentUpdateRef.current(data.content, data.userId);
        }
      })
      .on('broadcast', { event: 'live_content_change' }, (payload) => {
        const changeData = payload.payload as LocalContentChange;
        if (changeData.userId !== user.id && onLiveContentChangeRef.current) {
          onLiveContentChangeRef.current(changeData);
          
          setLiveContentChanges(prev => {
            const filtered = prev.filter(change => 
              !(change.userId === changeData.userId && 
                change.tableId === changeData.tableId &&
                change.cellType === changeData.cellType &&
                change.rowIndex === changeData.rowIndex &&
                change.colIndex === changeData.colIndex)
            );
            
            if (changeData.isTemporary && changeData.value) {
              return [...filtered, changeData];
            } else {
              return filtered;
            }
          });
        }
      })
      .on('broadcast', { event: 'editing_state' }, (payload) => {
        const editingData = payload.payload;
        if (editingData.userId !== user.id) {
          setEditingStates(prev => {
            if (editingData.action === 'start') {
              const filtered = prev.filter(state => 
                !(state.userId === editingData.userId && 
                  state.tableId === editingData.tableId &&
                  state.cellType === editingData.cellType &&
                  state.rowIndex === editingData.rowIndex &&
                  state.colIndex === editingData.colIndex)
              );
              return [...filtered, editingData];
            } else {
              return prev.filter(state => 
                !(state.userId === editingData.userId && 
                  state.tableId === editingData.tableId &&
                  state.cellType === editingData.cellType &&
                  state.rowIndex === editingData.rowIndex &&
                  state.colIndex === editingData.colIndex)
              );
            }
          });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          updateConnectionStatus('connected');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          updateConnectionStatus('disconnected');
          isConnectedRef.current = false;
        }
      });

    channelRef.current = channel;

    return () => {
      console.log(`🔌 Unsubscribing from data entry changes`);
      isConnectedRef.current = false;
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [entryId, user?.id, updateConnectionStatus]);

  // Set up presence tracking - FIXED VERSION
  useEffect(() => {
    if (!entryId || !user?.id) return;

    console.log(`👥 Setting up presence for data entry: ${entryId}, current user: ${user.id}`);

    const presenceChannel = supabase
      .channel(`data_entry_presence_${entryId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        const users: PresenceUser[] = [];
        
        console.log('🔍 Raw presence state:', state);
        console.log('🔍 Current user ID:', userIdRef.current);
        console.log('🔍 Presence state keys:', Object.keys(state));
        
        // Process presence state more carefully
        Object.entries(state).forEach(([presenceKey, presences]) => {
          console.log(`🔍 Processing presence key: "${presenceKey}"`);
          console.log(`🔍 Presences array:`, presences);
          
          if (Array.isArray(presences) && presences.length > 0) {
            const presence = presences[0] as any;
            console.log(`🔍 First presence object:`, presence);
            
            // FIXED: Check the actual userId from the presence object, not the key
            const actualUserId = presence?.userId || presenceKey;
            console.log(`🔍 Actual user ID: "${actualUserId}", Current user ID: "${userIdRef.current}"`);
            console.log(`🔍 Are they equal? ${actualUserId === userIdRef.current}`);
            
            if (actualUserId !== userIdRef.current) {
              console.log(`✅ Adding user to presence list:`, { actualUserId, presence });
              
              users.push({
                userId: actualUserId,
                userName: presence.userName || 'Unknown User',
                userColor: presence.userColor || '#6B7280',
                online_at: presence.online_at || new Date().toISOString()
              });
            } else {
              console.log(`⏭️ Skipping current user: ${actualUserId}`);
            }
          } else {
            console.log(`⚠️ Invalid presences array for key ${presenceKey}:`, presences);
          }
        });
        
        console.log(`👥 Final presence users (excluding self):`, users);
        console.log(`👥 Final user count:`, users.length);
        setPresenceUsers(users);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('👋 User joined:', key, newPresences);
        // Only log if it's not the current user
        if (key !== userIdRef.current) {
          console.log('👋 Other user joined data entry:', key);
        }
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('👋 User left:', key, leftPresences);
        // Only log if it's not the current user
        if (key !== userIdRef.current) {
          console.log('👋 Other user left data entry:', key);
        }
      })
      .subscribe(async (status) => {
        console.log('📡 Presence subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          // Track this user's presence
          const presenceData = {
            userId: user.id,
            userName: getDisplayName(),
            userColor: userColor,
            online_at: new Date().toISOString(),
            editingDataEntry: entryId,
            contextId: entryId,
            contextType: 'data_entry'
          };
          
          console.log('📡 Tracking presence for current user:', presenceData);
          
          await presenceChannel.track(presenceData);
        }
      });

    presenceChannelRef.current = presenceChannel;

    return () => {
      console.log(`🔌 Unsubscribing from data entry presence`);
      if (presenceChannelRef.current) {
        presenceChannelRef.current.unsubscribe();
        presenceChannelRef.current = null;
      }
    };
  }, [entryId, user?.id, getDisplayName, userColor]);

  // Broadcast content changes
  const broadcastContentChange = useCallback((
    content: TableContent,
    changeType: 'table_update' | 'table_add' | 'table_delete' | 'notes_update' | 'full_sync',
    tableId?: string,
    tableData?: any
  ) => {
    if (!channelRef.current || !user?.id || !isConnectedRef.current) {
      console.log('⚠️ Cannot broadcast: channel not ready');
      return;
    }

    const payload = {
      type: changeType,
      content,
      tableId,
      tableData,
      userId: user.id,
      userName: getDisplayName(),
      timestamp: new Date().toISOString()
    };

    channelRef.current.send({
      type: 'broadcast',
      event: 'content_change',
      payload
    }).catch((error: any) => {
      console.error('❌ Failed to broadcast content change:', error);
    });
  }, [user?.id, getDisplayName]);

  // Broadcast live content changes
  const broadcastLiveContentChange = useCallback((change: {
    tableId: string;
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes';
    rowIndex?: number;
    colIndex?: number;
    value: string;
    isTemporary: boolean;
  }) => {
    if (!channelRef.current || !user?.id || !isConnectedRef.current) {
      return;
    }

    const payload: LocalContentChange = {
      ...change,
      userId: user.id,
      userName: getDisplayName(),
      userColor,
      timestamp: new Date().toISOString()
    };

    channelRef.current.send({
      type: 'broadcast',
      event: 'live_content_change',
      payload
    }).catch((error: any) => {
      console.error('❌ Failed to broadcast live content change:', error);
    });
  }, [user?.id, getDisplayName, userColor]);

  // Broadcast editing state
  const broadcastEditingState = useCallback((editingInfo: {
    tableId: string;
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes';
    rowIndex?: number;
    colIndex?: number;
  }) => {
    if (!channelRef.current || !user?.id || !isConnectedRef.current) {
      return;
    }

    const payload = {
      ...editingInfo,
      userId: user.id,
      userName: getDisplayName(),
      userColor,
      timestamp: new Date().toISOString(),
      action: 'start'
    };

    channelRef.current.send({
      type: 'broadcast',
      event: 'editing_state',
      payload
    }).catch((error: any) => {
      console.error('❌ Failed to broadcast editing state:', error);
    });

    // Auto-clear after 5 seconds
    setTimeout(() => {
      if (channelRef.current && isConnectedRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'editing_state',
          payload: { ...payload, action: 'stop' }
        }).catch(() => {});
      }
    }, 5000);
  }, [user?.id, getDisplayName, userColor]);

  // Function to get live content for a specific cell
  const getLiveContentForCell = useCallback((
    tableId: string,
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes',
    rowIndex?: number,
    colIndex?: number
  ) => {
    return liveContentChanges.find(change =>
      change.tableId === tableId &&
      change.cellType === cellType &&
      change.rowIndex === rowIndex &&
      change.colIndex === colIndex &&
      change.isTemporary
    );
  }, [liveContentChanges]);

  // Set current content
  const setCurrentContent = useCallback((content: TableContent) => {
    currentContentRef.current = content;
  }, []);

  return {
    connectionStatus,
    presenceUsers,
    editingStates,
    liveContentChanges,
    userColor,
    broadcastContentChange,
    broadcastLiveContentChange,
    broadcastEditingState,
    getLiveContentForCell,
    setCurrentContent
  };
}

// Auto-save hook - unchanged
export function useDataEntryAutosave(
  content: TableContent,
  saveFunction: (content: TableContent) => Promise<void>,
  delay: number = 1000
) {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');

  useEffect(() => {
    const currentContentString = JSON.stringify(content);
    
    if (currentContentString === lastSavedRef.current) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('💾 Auto-saving data entry...');
        await saveFunction(content);
        lastSavedRef.current = currentContentString;
        console.log('✅ Data entry auto-saved successfully');
      } catch (error) {
        console.error('❌ Data entry auto-save failed:', error);
      }
    }, delay);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, saveFunction, delay]);
}